// ─── CV Layout Tokens (shared between native + web) ──────────────────

type CVLayoutStyle = 'MODERN' | 'CLASSIC' | 'EXECUTIVE' | 'CREATIVE';

export interface CVPrintLayoutTokens {
  /** True A4 margin from the CV theme specification. */
  pageMarginMm: number;
  /** Content voice used for body copy and item titles. */
  contentFontFamily: 'serif' | 'sans-serif';
  /** Supporting voice used for labels and metadata (Executive is mixed-family). */
  scaffoldFontFamily: 'serif' | 'sans-serif';
  displaySize: number;
  roleSize: number;
  sectionLabelSize: number;
  itemTitleSize: number;
  itemMetaSize: number;
  bodySize: number;
  captionSize: number;
  sectionGap: number;
  labelToContentGap: number;
  itemGap: number;
  photoSizeMm: number;
}

export interface CVLayoutTokens {
  // Fonts
  fontFamily: 'serif' | 'sans-serif';
  // Header
  nameSize: number;
  nameWeight: '400' | '600' | '700' | '800';
  nameUppercase: boolean;
  nameUsesAccent: boolean;
  photoShape: 'round' | 'square';
  photoBorder: boolean;
  // Contact
  contactShowIcons: boolean;
  contactSeparator: ' \u2022 ' | ' | ' | '';
  // When true (and icons are off), the contact row renders as wrapped chips with
  // social links in the accent colour (Web-CV / onboarding look). Optional;
  // the built-in styles omit it and keep the joined single-line rendering.
  contactLinkUsesAccent?: boolean;
  // Section headers
  sectionHeaderSize: number;
  sectionHeaderWeight: '600' | '700' | '800';
  sectionHeaderUsesAccent: boolean;
  sectionHeaderBorderUsesAccent: boolean;
  sectionHeaderRuleWidth: 'none' | 'full' | 'sixty-percent';
  sectionHeaderHasBar: boolean; // CREATIVE vertical bar
  // Optional accent-bar dimensions (used when sectionHeaderHasBar is true).
  // Omitted by the built-in styles, which fall back to the renderer defaults;
  // callers (e.g. the onboarding preview) can override to match a spec.
  sectionBarWidth?: number;
  sectionBarHeight?: number;
  sectionBarRadius?: number;
  // Skills
  skillsAsPills: boolean;
  skillPillUsesAccent: boolean; // CREATIVE: accent bg, else gray
  skillPillRadius: number;
  // Experience
  bulletUsesAccent: boolean;
  overviewItalic: boolean;
  // Spacing (screen px, shared by the DOM and native renderers)
  sectionGap: number;
  contentPadding: number;
  /** Gap between sibling items inside a section (experience entries etc.). */
  itemGap: number;
  /** Section header block → first content row. */
  labelToContentGap: number;
  // Body copy (screen px). Line-height is the LTR value; RTL adds
  // CV_SCREEN_RHYTHM.rtlLineHeightBoost for Cairo's taller glyph extents.
  bodySize: number;
  bodyLineHeight: number;
  // Creative-specific
  hasColoredHeader: boolean;
  /** Print/DOCX contract shared by every export renderer. Sizes are points. */
  print: CVPrintLayoutTokens;
}

const MODERN: CVLayoutTokens = {
  fontFamily: 'sans-serif',
  nameSize: 28,
  nameWeight: '700',
  nameUppercase: false,
  nameUsesAccent: false,
  photoShape: 'round',
  photoBorder: false,
  contactShowIcons: false,
  contactSeparator: ' | ',
  sectionHeaderSize: 14,
  sectionHeaderWeight: '700',
  sectionHeaderUsesAccent: true,
  sectionHeaderBorderUsesAccent: false,
  sectionHeaderRuleWidth: 'none',
  sectionHeaderHasBar: false,
  skillsAsPills: false,
  skillPillUsesAccent: false,
  skillPillRadius: 0,
  bulletUsesAccent: false,
  overviewItalic: false,
  sectionGap: 22,
  contentPadding: 20,
  itemGap: 16,
  labelToContentGap: 8,
  bodySize: 14,
  bodyLineHeight: 20,
  hasColoredHeader: false,
  print: {
    // Reference-calibrated density (2026-08-27): margins and gaps tightened
    // against the best-in-class two-page benchmark CV so a full career fits
    // two pages without shrinking type. The fit-to-page ladder still floors
    // the margin at 12mm.
    pageMarginMm: 12.5,
    contentFontFamily: 'sans-serif',
    scaffoldFontFamily: 'sans-serif',
    displaySize: 20,
    roleSize: 11.5,
    sectionLabelSize: 10,
    itemTitleSize: 11,
    itemMetaSize: 9.5,
    bodySize: 10.5,
    captionSize: 8.5,
    sectionGap: 11,
    labelToContentGap: 5.5,
    itemGap: 7.5,
    photoSizeMm: 22,
  },
};

const CLASSIC: CVLayoutTokens = {
  fontFamily: 'serif',
  nameSize: 26,
  nameWeight: '700',
  nameUppercase: true,
  nameUsesAccent: true,
  photoShape: 'square',
  photoBorder: true,
  contactShowIcons: false,
  contactSeparator: ' | ',
  sectionHeaderSize: 13,
  sectionHeaderWeight: '700',
  sectionHeaderUsesAccent: false,
  sectionHeaderBorderUsesAccent: true,
  sectionHeaderRuleWidth: 'full',
  sectionHeaderHasBar: false,
  skillsAsPills: false,
  skillPillUsesAccent: false,
  skillPillRadius: 0,
  bulletUsesAccent: false,
  overviewItalic: true,
  sectionGap: 20,
  contentPadding: 20,
  itemGap: 14,
  labelToContentGap: 8,
  bodySize: 14,
  bodyLineHeight: 20,
  hasColoredHeader: false,
  print: {
    pageMarginMm: 13.5,
    contentFontFamily: 'serif',
    scaffoldFontFamily: 'serif',
    displaySize: 19,
    roleSize: 11.5,
    sectionLabelSize: 9.5,
    itemTitleSize: 11,
    itemMetaSize: 9.5,
    bodySize: 10.5,
    captionSize: 8.5,
    sectionGap: 10,
    labelToContentGap: 5.5,
    itemGap: 7,
    photoSizeMm: 20,
  },
};

const EXECUTIVE: CVLayoutTokens = {
  fontFamily: 'serif',
  nameSize: 32,
  nameWeight: '400',
  nameUppercase: false,
  nameUsesAccent: false,
  photoShape: 'square',
  photoBorder: true,
  contactShowIcons: false,
  contactSeparator: ' \u2022 ',
  sectionHeaderSize: 12,
  sectionHeaderWeight: '700',
  sectionHeaderUsesAccent: true,
  sectionHeaderBorderUsesAccent: true,
  sectionHeaderRuleWidth: 'sixty-percent',
  sectionHeaderHasBar: false,
  skillsAsPills: false,
  skillPillUsesAccent: false,
  skillPillRadius: 0,
  bulletUsesAccent: false,
  overviewItalic: true,
  sectionGap: 26,
  contentPadding: 20,
  itemGap: 18,
  labelToContentGap: 9,
  bodySize: 14,
  bodyLineHeight: 20,
  hasColoredHeader: false,
  print: {
    pageMarginMm: 14.5,
    contentFontFamily: 'serif',
    scaffoldFontFamily: 'sans-serif',
    displaySize: 19.5,
    roleSize: 10.5,
    sectionLabelSize: 9,
    itemTitleSize: 11,
    itemMetaSize: 9,
    bodySize: 10.5,
    captionSize: 8.5,
    sectionGap: 14,
    labelToContentGap: 6.5,
    itemGap: 8.5,
    photoSizeMm: 20,
  },
};

const CREATIVE: CVLayoutTokens = {
  fontFamily: 'sans-serif',
  nameSize: 26,
  nameWeight: '700',
  nameUppercase: false,
  nameUsesAccent: false, // Creative headers always use white text on the accent band.
  photoShape: 'round',
  photoBorder: false,
  contactShowIcons: true,
  contactSeparator: '',
  sectionHeaderSize: 17,
  sectionHeaderWeight: '800',
  sectionHeaderUsesAccent: false,
  sectionHeaderBorderUsesAccent: false,
  sectionHeaderRuleWidth: 'none',
  sectionHeaderHasBar: true,
  skillsAsPills: true,
  skillPillUsesAccent: true,
  skillPillRadius: 0,
  bulletUsesAccent: true,
  overviewItalic: false,
  sectionGap: 22,
  contentPadding: 20,
  itemGap: 16,
  labelToContentGap: 8,
  bodySize: 14,
  bodyLineHeight: 20,
  hasColoredHeader: true,
  print: {
    pageMarginMm: 12.5,
    contentFontFamily: 'sans-serif',
    scaffoldFontFamily: 'sans-serif',
    displaySize: 20,
    roleSize: 11.5,
    sectionLabelSize: 10.5,
    itemTitleSize: 11,
    itemMetaSize: 9.5,
    bodySize: 10.5,
    captionSize: 8.5,
    sectionGap: 11,
    labelToContentGap: 5.5,
    itemGap: 7.5,
    photoSizeMm: 22,
  },
};

export const LAYOUT_MAP: Record<CVLayoutStyle, CVLayoutTokens> = {
  MODERN,
  CLASSIC,
  EXECUTIVE,
  CREATIVE,
};

export function getLayoutTokens(style: CVLayoutStyle): CVLayoutTokens {
  return LAYOUT_MAP[style] || MODERN;
}

/**
 * Template-independent screen rhythm (px), shared by the DOM renderer
 * (`packages/public-renderers/src/cv.tsx`) and the native previews so the CV
 * document keeps one compact, paper-like density everywhere. Change values
 * here — never re-hardcode them inside a renderer.
 */
/**
 * Screen-density compression for `CVConfigData.fitToOnePage` on surfaces with
 * no page metaphor (the native previews): the big vertical levers tighten so
 * the toggle gives immediate visual feedback, while type stays at full size —
 * shrinking fonts on a scrolling phone screen would only hurt legibility. The
 * DOM renderer instead scales geometrically to the real one-page sheet, and
 * the PDF export runs its own print-token ladder (fit-to-page.ts).
 */
export function compressCvScreenTokens(
  tokens: CVLayoutTokens,
  factor = 0.7,
): CVLayoutTokens {
  return {
    ...tokens,
    sectionGap: Math.round(tokens.sectionGap * factor),
    itemGap: Math.round(tokens.itemGap * factor),
    labelToContentGap: Math.round(tokens.labelToContentGap * factor),
  };
}

export const CV_SCREEN_RHYTHM = {
  /** Extra body line-height for RTL/Cairo glyph extents. */
  rtlLineHeightBoost: 4,
  educationGap: 12,
  certificationGap: 10,
  skillGroupGap: 12,
  highlightGap: 6,
  languageGap: 4,
  bulletGap: 3,
  bulletListGapTop: 6,
  overviewGapTop: 6,
  headerContactGapTop: 12,
  headerSocialGapTop: 10,
  headerPhotoGap: 16,
  photoSize: 72,
} as const;

// ─── Color utilities ──────────────────────────────────────────────────

export const COLOR_MAP: Record<string, string> = {
  BLACK: '#0A0A0A',
  BLUE: '#2563EB',
  PURPLE: '#5A2AFF',
  EMERALD: '#059669',
  RED: '#C8102E',
  ORANGE: '#EA580C',
};

export function getAccentHex(colorId: string): string {
  // BLACK is the default for new or unconfigured CVs (see
  // docs/cv-theme-system-architecture.md), so unknown ids resolve to it too.
  return COLOR_MAP[colorId] || COLOR_MAP.BLACK;
}

export function isColorDark(hex: string): boolean {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.65;
}
